
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import {
  SortDirection,
  SupportTicketAssignmentFilterEnum,
  SupportTicketCloseReasonEnum,
  SupportTicketDto,
  SupportTicketMessageDto,
  SupportTicketsClient,
  SupportTicketStatusEnum,
  SupportTicketSystemMessageEnum,
} from '@/services/api-clients';
import SupportChatHead from './SupportChatHead.vue';
import SupportChatHeadOverflow from './SupportChatHeadOverflow.vue';
import SupportChatHeadsGrip from './SupportChatHeadsGrip.vue';
import SupportChatPopup from './SupportChatPopup.vue';
import { driverChatHub } from '@/services/driver-chat-hub';
import { supportTicketViewRegistry } from '@/services/support-ticket-view-registry';
import { resolveSupportChatHeadColors } from '@/helpers/supportChatHeadColor.helper';
import { GETTERS, MUTATIONS } from '@/store/namespaces';
import { RouteHelper } from '@/router/route.helper';

const MAX_VISIBLE_CHATHEADS = 5;
const DOCK_TOP_STORAGE_KEY = 'support_chat_head_dock_top';
const DOCK_EDGE_PADDING = 24;
const DOCK_FALLBACK_HEIGHT = 96;
const DRAG_CLICK_SLOP = 3;
const OVERFLOW_MENU_MAX_HEIGHT = 320;
const OVERFLOW_MENU_MIN_HEIGHT = 120;
const OVERFLOW_MENU_ITEM_HEIGHT = 41;
const OVERFLOW_MENU_VERTICAL_PADDING = 12;
const OVERFLOW_MENU_GAP = 8;

@Component({
  components: {
    SupportChatHead,
    SupportChatHeadOverflow,
    SupportChatHeadsGrip,
    SupportChatPopup,
  },
})
export default class SupportChatHeads extends Vue {
  @Getter(GETTERS.USER_ID) userId!: string;

  @Getter(GETTERS.USER_DISPLAY_NAME) userDisplayName!: string;

  @Getter(GETTERS.SUPPORT_CHAT_POPUP_REQUEST) popupRequest!: number | null;

  myActiveTickets: SupportTicketDto[] = [];

  unreadCounters: Record<number, number> = {};

  messagesPerTicket: Record<number, SupportTicketMessageDto[]> = {};

  deletedByAuthors: Record<number, string> = {};

  // Tickets that were claimed by another employee while this employee had their popup open.
  // We delay removal from the stack until the popup is closed so the system message in the chat
  // remains visible. Only ids are stored — the message content already lives in messagesPerTicket.
  pendingClaimRemovals: number[] = [];

  openPopupTicketId: number | null = null;

  popupOpen = false;

  expanded = false;

  overflowMenuOpen = false;

  gripPulse = false;

  isDragging = false;

  dockTop: number | null = null;

  private gripPulseTimeout: number | null = null;

  private dragStartPointerY = 0;

  private dragStartTop = 0;

  private dragMoved = false;

  overflowMenuPlacement: 'top' | 'bottom' = 'top';

  overflowMenuMaxHeight = OVERFLOW_MENU_MAX_HEIGHT;

  private unsubscribeMessage: (() => void) | null = null;

  private unsubscribeClaimed: (() => void) | null = null;

  private unsubscribeClosed: (() => void) | null = null;

  private unsubscribeAssignedToMe: (() => void) | null = null;

  private unsubscribeDeleted: (() => void) | null = null;

  get visibleTickets(): SupportTicketDto[] {
    return this.myActiveTickets.slice(0, MAX_VISIBLE_CHATHEADS);
  }

  get overflowTickets(): SupportTicketDto[] {
    return this.myActiveTickets.slice(MAX_VISIBLE_CHATHEADS);
  }

  get hasOverflow(): boolean {
    return this.myActiveTickets.length > MAX_VISIBLE_CHATHEADS;
  }

  get overflowCount(): number {
    return this.myActiveTickets.length - MAX_VISIBLE_CHATHEADS;
  }

  get hasOverflowUnread(): boolean {
    return this.overflowTickets.some((t) => (this.unreadCounters[t.id ?? 0] ?? 0) > 0);
  }

  get totalUnreadCount(): number {
    return Object.values(this.unreadCounters).reduce((sum, value) => sum + value, 0);
  }

  get overflowMenuStyle(): Record<string, string> {
    const style: Record<string, string> = { maxHeight: `${this.overflowMenuMaxHeight}px` };
    const anchor = `calc(100% + ${OVERFLOW_MENU_GAP}px)`;
    if (this.overflowMenuPlacement === 'top') style.bottom = anchor;
    else style.top = anchor;
    return style;
  }

  get dockStyle(): Record<string, string> {
    // Null means the dock has never been dragged, so the CSS default position applies.
    return this.dockTop === null ? {} : { top: `${this.dockTop}px` };
  }

  get chatHeadColors(): Record<number, string> {
    const ids = this.myActiveTickets
      .map((t) => t.id)
      .filter((id): id is number => id != null);
    return resolveSupportChatHeadColors(ids);
  }

  get isOnSupportTicketDetailsRoute(): boolean {
    return this.$route.name === 'Support Ticket Details';
  }

  get openPopupTicket(): SupportTicketDto | null {
    if (this.openPopupTicketId === null) return null;
    return this.myActiveTickets.find((t) => t.id === this.openPopupTicketId) ?? null;
  }

  @Watch('userId', { immediate: true })
  onUserIdChanged(newId: string) {
    if (newId) {
      this.initialize();
    } else {
      this.teardown();
    }
  }

  // After the popup closes, drop tickets that were claimed by another employee while the window
  // was open — the system message in the chat already informed the user, closing the window is
  // the acknowledgment that lets us remove the chathead from the stack.
  // Reply on a notification: open the conversation here when it is one of my chat heads and
  // the dock is visible; otherwise fall back to the ticket page.
  @Watch('popupRequest')
  async onPopupRequest(ticketId: number | null) {
    if (ticketId === null) return;
    this.$store.commit(MUTATIONS.REQUEST_SUPPORT_CHAT_POPUP, null);
    if (!this.isOnSupportTicketDetailsRoute && !this.myActiveTickets.some((t) => t.id === ticketId)) {
      // The dock list is loaded once; a conversation assigned without a hub event would be missing.
      await this.fetchMyActiveTickets();
      await this.joinAllTickets();
    }
    if (!this.isOnSupportTicketDetailsRoute && this.myActiveTickets.some((t) => t.id === ticketId)) {
      this.collapseAll();
      this.openPopupForTicket(ticketId);
      return;
    }
    this.$router.push(RouteHelper.SupportTicketDetails(ticketId));
  }

  // Opening the ticket page reads the conversation, like opening its popup does.
  @Watch('$route.params', { immediate: true })
  onRouteParamsChanged() {
    if (!this.isOnSupportTicketDetailsRoute) return;
    const ticketId = Number(this.$route.params.id);
    if (this.unreadCounters[ticketId]) {
      this.unreadCounters = { ...this.unreadCounters, [ticketId]: 0 };
    }
  }

  @Watch('popupOpen')
  @Watch('openPopupTicketId')
  onPopupShownTicketChanged() {
    supportTicketViewRegistry.setShown('popup', this.popupOpen ? this.openPopupTicketId : null);
  }

  @Watch('popupOpen')
  onPopupOpenChanged(open: boolean) {
    if (open) return;
    const previousId = this.openPopupTicketId;
    if (previousId !== null && this.pendingClaimRemovals.includes(previousId)) {
      this.removeTicket(previousId);
    }
  }

  mounted() {
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    window.addEventListener('resize', this.onWindowResize);
    this.restoreDockTop();
  }

  beforeDestroy() {
    supportTicketViewRegistry.setShown('popup', null);
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    window.removeEventListener('resize', this.onWindowResize);
    this.stopDragListening();
    this.teardown();
  }

  onDragStart(event: MouseEvent) {
    const root = this.$refs.root as HTMLElement | undefined;
    if (!root) return;

    this.isDragging = true;
    this.dragMoved = false;
    this.dragStartPointerY = event.clientY;
    this.dragStartTop = root.getBoundingClientRect().top;

    document.addEventListener('mousemove', this.onDragMove);
    document.addEventListener('mouseup', this.onDragEnd);
  }

  onDragMove(event: MouseEvent) {
    const delta = event.clientY - this.dragStartPointerY;
    // A few pixels of slop, so a slightly shaky click still counts as a click.
    if (Math.abs(delta) > DRAG_CLICK_SLOP) this.dragMoved = true;
    this.dockTop = this.clampDockTop(this.dragStartTop + delta);
  }

  onDragEnd() {
    this.stopDragListening();
    this.isDragging = false;
    if (this.overflowMenuOpen) this.updateOverflowMenuPlacement();
    // Persist once, on release. Writing on every mousemove would hammer localStorage.
    if (this.dragMoved && this.dockTop !== null) {
      localStorage.setItem(DOCK_TOP_STORAGE_KEY, String(Math.round(this.dockTop)));
    }
  }

  private stopDragListening() {
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.onDragEnd);
  }

  private clampDockTop(value: number): number {
    const root = this.$refs.root as HTMLElement | undefined;
    const height = root?.getBoundingClientRect().height ?? DOCK_FALLBACK_HEIGHT;
    const max = window.innerHeight - height - DOCK_EDGE_PADDING;
    return Math.min(Math.max(value, DOCK_EDGE_PADDING), Math.max(DOCK_EDGE_PADDING, max));
  }

  private restoreDockTop() {
    const stored = localStorage.getItem(DOCK_TOP_STORAGE_KEY);
    if (stored === null) return;
    const parsed = Number(stored);
    if (Number.isNaN(parsed)) return;
    // Re-clamp on load: the saved position may come from a taller window or another screen.
    this.$nextTick(() => {
      this.dockTop = this.clampDockTop(parsed);
    });
  }

  private onWindowResize() {
    if (this.dockTop !== null) this.dockTop = this.clampDockTop(this.dockTop);
    if (this.overflowMenuOpen) this.updateOverflowMenuPlacement();
  }

  async initialize() {
    await this.waitForHubConnected();
    await this.fetchMyActiveTickets();
    await this.joinAllTickets();
    this.subscribeHubEvents();
  }

  teardown() {
    if (this.unsubscribeMessage) this.unsubscribeMessage();
    if (this.unsubscribeClaimed) this.unsubscribeClaimed();
    if (this.unsubscribeClosed) this.unsubscribeClosed();
    if (this.unsubscribeAssignedToMe) this.unsubscribeAssignedToMe();
    if (this.unsubscribeDeleted) this.unsubscribeDeleted();
    this.unsubscribeMessage = null;
    this.unsubscribeClaimed = null;
    this.unsubscribeClosed = null;
    this.unsubscribeAssignedToMe = null;
    this.unsubscribeDeleted = null;
    this.myActiveTickets = [];
    this.unreadCounters = {};
    this.messagesPerTicket = {};
    this.deletedByAuthors = {};
    this.pendingClaimRemovals = [];
    this.openPopupTicketId = null;
    this.popupOpen = false;
    this.expanded = false;
    this.overflowMenuOpen = false;
    if (this.gripPulseTimeout) window.clearTimeout(this.gripPulseTimeout);
    this.gripPulse = false;
  }

  async waitForHubConnected(timeoutMs = 5000): Promise<void> {
    const start = Date.now();
    while (!driverChatHub.isConnected()) {
      if (Date.now() - start > timeoutMs) return;
      await new Promise((resolve) => { setTimeout(resolve, 200); });
    }
  }

  fetchMyActiveTickets() {
    return new SupportTicketsClient().getTickets({
      skip: 0,
      take: 50,
      sortItems: [{ propertyName: 'ModificationDate', sortDirection: SortDirection.Desc }],
      assignmentFilter: SupportTicketAssignmentFilterEnum.Me,
      statusId: SupportTicketStatusEnum.InProgress,
    }).then(({ success, result }) => {
      if (success && result?.items) {
        this.myActiveTickets = result.items;
      }
    });
  }

  async joinAllTickets() {
    for (const ticket of this.myActiveTickets) {
      if (ticket.id == null) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        await driverChatHub.joinTicket(ticket.id);
      } catch {
        // hub.start() już zalogował do Sentry; chathead działa bez real-time push
      }
    }
  }

  subscribeHubEvents() {
    this.unsubscribeMessage = driverChatHub.onMessageReceived((msg) => this.onMessageReceived(msg));
    this.unsubscribeClaimed = driverChatHub.onTicketClaimed((id, name) => this.onTicketClaimed(id, name));
    this.unsubscribeClosed = driverChatHub.onTicketClosed((id, reason) => this.onTicketClosed(id, reason));
    this.unsubscribeAssignedToMe = driverChatHub.onTicketAssignedToMe((ticket) => this.onTicketAssignedToMe(ticket));
    this.unsubscribeDeleted = driverChatHub.onTicketDeleted((id, name) => this.onTicketDeleted(id, name));
  }

  async onTicketAssignedToMe(ticket: SupportTicketDto) {
    if (ticket.id == null) return;
    if (this.myActiveTickets.some((t) => t.id === ticket.id)) return;
    // Prepend so the newly assigned ticket shows up at the top of the stack
    this.myActiveTickets = [ticket, ...this.myActiveTickets];
    try {
      await driverChatHub.joinTicket(ticket.id);
    } catch {
      // hub may be reconnecting; chathead is still rendered, real-time push will come after rejoin
    }
  }

  onMessageReceived(msg: SupportTicketMessageDto) {
    if (msg.supportTicketId == null) return;
    const ticketId = msg.supportTicketId;
    const ticket = this.myActiveTickets.find((t) => t.id === ticketId);
    if (!ticket) return;

    this.applyUploadPermission(ticket, msg);

    // A file saved to driver files comes again with the same id and replaces the earlier copy;
    // a freshly sent file is never in driver files yet, so that flag marks the update too.
    let isUpdate = !!msg.isAttachmentInDriverFiles;
    if (this.messagesPerTicket[ticketId]) {
      const current = this.messagesPerTicket[ticketId];
      isUpdate = isUpdate || current.some((m) => m.id === msg.id);
      this.messagesPerTicket = {
        ...this.messagesPerTicket,
        [ticketId]: current.some((m) => m.id === msg.id)
          ? current.map((m) => (m.id === msg.id ? msg : m))
          : [...current, msg],
      };
    }
    if (isUpdate) return;

    // Activity confirmations arrive every few minutes while the driver keeps the chat open.
    // They belong in the transcript, but not in the unread badge.
    const isActivityCheck = msg.systemMessageId === SupportTicketSystemMessageEnum.ActivityCheck;
    // A conversation on screen in this tab, in the popup or on the ticket page, is read as it arrives.
    if (msg.userId !== this.userId && !supportTicketViewRegistry.isShownHere(ticketId) && !isActivityCheck) {
      this.unreadCounters = {
        ...this.unreadCounters,
        [ticketId]: (this.unreadCounters[ticketId] ?? 0) + 1,
      };
      this.triggerGripPulse();
    }
  }

  // A message of an older, ended permission must not hide the active one.
  private applyUploadPermission(ticket: SupportTicketDto, msg: SupportTicketMessageDto) {
    const permission = msg.uploadPermission;
    if (!permission) return;
    let activeUploadPermission = ticket.activeUploadPermission;
    if (permission.isActive) {
      activeUploadPermission = permission;
    } else if (activeUploadPermission?.id === permission.id) {
      activeUploadPermission = undefined;
    } else {
      return;
    }
    this.myActiveTickets = this.myActiveTickets.map((t) => (t.id === ticket.id
      ? { ...t, activeUploadPermission } as SupportTicketDto
      : t));
  }

  private triggerGripPulse() {
    if (this.expanded) return;
    // Clearing the class first is required: a CSS animation does not replay unless it is removed.
    this.gripPulse = false;
    this.$nextTick(() => {
      this.gripPulse = true;
      if (this.gripPulseTimeout) window.clearTimeout(this.gripPulseTimeout);
      this.gripPulseTimeout = window.setTimeout(() => {
        this.gripPulse = false;
      }, 2000);
    });
  }

  onTicketClaimed(ticketId: number, employeeFullName: string) {
    const ticket = this.myActiveTickets.find((t) => t.id === ticketId);
    if (!ticket) return;
    if (employeeFullName === this.userDisplayName) return;

    // Defer removal while the popup of this very conversation is open — the employee can read
    // the system message (already in messagesPerTicket) until they close the window themselves.
    const popupShowingThisTicket = this.popupOpen && this.openPopupTicketId === ticketId;
    if (popupShowingThisTicket) {
      if (!this.pendingClaimRemovals.includes(ticketId)) {
        this.pendingClaimRemovals = [...this.pendingClaimRemovals, ticketId];
      }
      return;
    }

    this.removeTicket(ticketId);
  }

  onTicketClosed(ticketId: number, _reason: SupportTicketCloseReasonEnum) {
    this.removeTicket(ticketId);
  }

  onTicketDeleted(ticketId: number, employeeFullName: string) {
    const ticket = this.myActiveTickets.find((t) => t.id === ticketId);
    if (!ticket) return;

    this.$set(ticket, 'isDeleted', true);
    this.deletedByAuthors = { ...this.deletedByAuthors, [ticketId]: employeeFullName };

    const popupShowingThisTicket = this.popupOpen && this.openPopupTicketId === ticketId;
    if (!popupShowingThisTicket) {
      this.unreadCounters = {
        ...this.unreadCounters,
        [ticketId]: (this.unreadCounters[ticketId] ?? 0) + 1,
      };
    }
  }

  async removeTicket(ticketId: number) {
    await driverChatHub.leaveTicket(ticketId).catch(() => undefined);
    this.myActiveTickets = this.myActiveTickets.filter((t) => t.id !== ticketId);
    const newCounters = { ...this.unreadCounters };
    delete newCounters[ticketId];
    this.unreadCounters = newCounters;
    const newMessages = { ...this.messagesPerTicket };
    delete newMessages[ticketId];
    this.messagesPerTicket = newMessages;
    this.pendingClaimRemovals = this.pendingClaimRemovals.filter((id) => id !== ticketId);
    if (this.openPopupTicketId === ticketId) {
      this.openPopupTicketId = null;
      this.popupOpen = false;
    }
  }

  onGripClick() {
    // Releasing after a drag also fires click; that must not toggle the bar.
    if (this.dragMoved) {
      this.dragMoved = false;
      return;
    }
    if (this.expanded) {
      this.collapseAll();
      return;
    }
    this.expanded = true;
  }

  onChatHeadClick(ticket: SupportTicketDto) {
    if (ticket.id == null) return;
    // The bar only renders while expanded, so a click on an avatar is always a pick,
    // never a request to expand. Picking one closes the bar so the portal stays uncovered.
    this.collapseAll();
    this.openPopupForTicket(ticket.id);
  }

  onOverflowClick() {
    if (!this.overflowMenuOpen) this.updateOverflowMenuPlacement();
    this.overflowMenuOpen = !this.overflowMenuOpen;
  }

  // The dock can sit anywhere on the Y axis after dragging, so the menu picks the side with room
  // instead of always opening upwards, and shrinks to whatever height is actually available.
  private updateOverflowMenuPlacement() {
    const root = this.$refs.root as HTMLElement | undefined;
    if (!root) return;

    const rect = root.getBoundingClientRect();
    const spaceAbove = rect.top - DOCK_EDGE_PADDING - OVERFLOW_MENU_GAP;
    const spaceBelow = window.innerHeight - rect.bottom - DOCK_EDGE_PADDING - OVERFLOW_MENU_GAP;

    const wanted = Math.min(
      OVERFLOW_MENU_MAX_HEIGHT,
      this.overflowCount * OVERFLOW_MENU_ITEM_HEIGHT + OVERFLOW_MENU_VERTICAL_PADDING,
    );

    let placeTop: boolean;
    if (spaceAbove >= wanted) placeTop = true;
    else if (spaceBelow >= wanted) placeTop = false;
    else placeTop = spaceAbove >= spaceBelow;

    this.overflowMenuPlacement = placeTop ? 'top' : 'bottom';
    const available = placeTop ? spaceAbove : spaceBelow;
    this.overflowMenuMaxHeight = Math.max(
      OVERFLOW_MENU_MIN_HEIGHT,
      Math.min(OVERFLOW_MENU_MAX_HEIGHT, available),
    );
  }

  onOverflowSelect(ticket: SupportTicketDto) {
    if (ticket.id == null) return;
    // Świadoma rotacja — only happens on user pick from overflow menu, never on SignalR push.
    this.myActiveTickets = [
      ticket,
      ...this.myActiveTickets.filter((t) => t.id !== ticket.id),
    ];
    this.collapseAll();
    this.openPopupForTicket(ticket.id);
  }

  openPopupForTicket(ticketId: number) {
    this.openPopupTicketId = ticketId;
    this.popupOpen = true;
    if (this.unreadCounters[ticketId]) {
      this.unreadCounters = { ...this.unreadCounters, [ticketId]: 0 };
    }
  }

  collapseAll() {
    this.overflowMenuOpen = false;
    this.expanded = false;
  }

  onDocumentMouseDown(event: MouseEvent) {
    if (!this.expanded && !this.overflowMenuOpen) return;
    const root = this.$refs.root as HTMLElement | undefined;
    if (root && !root.contains(event.target as Node)) {
      this.collapseAll();
    }
  }

  onMessagesLoaded(messages: SupportTicketMessageDto[]) {
    if (this.openPopupTicketId === null) return;
    this.messagesPerTicket = {
      ...this.messagesPerTicket,
      [this.openPopupTicketId]: messages,
    };
  }

  onOlderLoaded(older: SupportTicketMessageDto[]) {
    if (this.openPopupTicketId === null || older.length === 0) return;
    const ticketId = this.openPopupTicketId;
    const current = this.messagesPerTicket[ticketId] ?? [];
    this.messagesPerTicket = {
      ...this.messagesPerTicket,
      [ticketId]: [...older, ...current],
    };
  }

  onSendMessage({ ticketId, content }: { ticketId: number; content: string }) {
    new SupportTicketsClient().sendEmployeeMessage({ ticketId, content }).then(({ success, errors }) => {
      if (!success) {
        this.$notifier.error(errors ?? []);
      }
    });
  }
}
